import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth, isFullLogin } from '@/contexts/AuthContext';
import { OutboundBuddyLogo } from '@/components/brand/OutboundBuddyLogo';

interface ProtectedRouteProps {
  children: React.ReactNode;
  /** Backward-compatible: requires admin-level role (owner/admin/developer) */
  requireAdmin?: boolean;
  /** Minimum role level required (e.g. 'manager' allows manager, admin, developer, owner) */
  requireRole?: 'owner' | 'admin' | 'developer' | 'manager' | 'viewer' | 'client';
}

export function ProtectedRoute({ children, requireAdmin = false, requireRole }: ProtectedRouteProps) {
  const { user, session, profile, loading, hasMinRole } = useAuth();

  // Show loading state during authentication check
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <OutboundBuddyLogo size="lg" />
          <p className="mt-4 text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  // Only redirect to auth if we're sure there's no user (not during hot reloads)
  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  // A redeemed invite link is a real Supabase session, so without this check an
  // invitee could skip the Set Password form and use the app with the random
  // password minted at invite time (then be locked out next visit). Sessions
  // from an email link (token amr method "otp") with no accepted stamp are sent
  // to the Set Password screen; saving the password stamps the profile and
  // Auth.tsx refreshes it, so the next render passes.
  if (session && !isFullLogin(session) && profile && !profile.invitation_accepted_at) {
    return <Navigate to="/auth?reset=true" replace />;
  }

  // Check role-based access — only admin-level roles (owner/admin/developer) can access internal pages
  if (requireAdmin && !hasMinRole('admin')) {
    return <Navigate to="/dashboard" replace />;
  }

  if (requireRole && !hasMinRole(requireRole)) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
}